import { Alert, MenuItem, Stack, TextField } from "@mui/material"
import type { BiomeshEventResponse } from "@phc/common/gen/threatAnalyticsGen"
import {
  EventStatus,
  getListEventsBiomeshEventListGetQueryKey,
  useCreateEventBiomeshEventPost,
  useUpdateEventBiomeshEventEventIdPut,
} from "@phc/common/gen/threatAnalyticsGen"
import { useQueryClient } from "@tanstack/react-query"
import { enqueueSnackbar } from "notistack"
import type { FC } from "react"
import { useCallback, useState } from "react"
import { Modal } from "../Shared/Modal"
import { eventStatusOptions, extractApiError, isEventStatus } from "./copUtils"

interface EventFormDialogProps {
  open: boolean
  onClose: () => void
  sensorId: string
  event?: BiomeshEventResponse
}

export const EventFormDialog: FC<EventFormDialogProps> = ({
  open,
  onClose,
  sensorId,
  event,
}) => {
  const isEdit = !!event
  const queryClient = useQueryClient()

  const [name, setName] = useState(event?.name ?? "")
  const [status, setStatus] = useState<string>(
    event?.status ?? EventStatus.EVENT_STATUS_INITIAL
  )
  const [observations, setObservations] = useState(event?.observations ?? "")
  const [resources, setResources] = useState(event?.resources ?? "")
  const [formError, setFormError] = useState("")

  const { mutateAsync: createEvent, isPending: createPending } =
    useCreateEventBiomeshEventPost()
  const { mutateAsync: updateEvent, isPending: updatePending } =
    useUpdateEventBiomeshEventEventIdPut()

  const isPending = createPending || updatePending

  const handleSave = useCallback(async () => {
    setFormError("")

    if (!name.trim()) {
      setFormError("Event name is required")
      return
    }

    const validatedStatus = isEventStatus(status) ? status : null

    const payload = {
      sensor_id: sensorId,
      name: name.trim(),
      status: validatedStatus,
      observations: observations.trim() || null,
      resources: resources.trim() || null,
    }

    try {
      if (event) {
        await updateEvent({ eventId: event.event_id, data: payload })
        enqueueSnackbar("Event updated", { variant: "success" })
      } else {
        await createEvent({ data: payload })
        enqueueSnackbar("Event created", { variant: "success" })
      }
      await queryClient.invalidateQueries({
        queryKey: getListEventsBiomeshEventListGetQueryKey(),
      })
      onClose()
    } catch (err) {
      setFormError(extractApiError(err, "Failed to save event"))
    }
  }, [
    name,
    status,
    observations,
    resources,
    sensorId,
    event,
    createEvent,
    updateEvent,
    queryClient,
    onClose,
  ])

  return (
    <Modal
      open={open}
      onClose={onClose}
      onConfirm={() => void handleSave()}
      title={isEdit ? "Edit Event" : "New Event"}
      confirmLabel="Save"
      disableBtns={isPending}
    >
      <Stack spacing={2} sx={{ mt: 1, minWidth: 400 }}>
        {formError && <Alert severity="error">{formError}</Alert>}

        <TextField
          label="Event Name"
          value={name}
          onChange={e => setName(e.target.value)}
          required
          fullWidth
        />

        <TextField
          label="Status"
          value={status}
          onChange={e => setStatus(e.target.value)}
          select
          fullWidth
        >
          {eventStatusOptions.map(opt => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Observations"
          value={observations}
          onChange={e => setObservations(e.target.value)}
          multiline
          minRows={3}
          fullWidth
        />

        <TextField
          label="Resources"
          value={resources}
          onChange={e => setResources(e.target.value)}
          multiline
          minRows={2}
          fullWidth
        />
      </Stack>
    </Modal>
  )
}
